"use client";

import { LocaleLink } from "@i18n/routing";
import { AssetToolbar } from "@saas/shared/assets/AssetToolbar";
import { cn } from "@ui/lib";
import { ArrowUpRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { HISTORY_FILTERS, type HistoryFilter } from "./types";

export type FeedView = "examples" | "templates" | "mine";

/** 工作台右栏顶上的「示例 / 模板 / 我的作品」。之前登录用户一有历史，示例区就整个被
 *  历史顶掉、再也回不去（10/22）。Templates 和 History 一样常驻（10/23），访客没有作品只给前两个。
 *  全部 / 图片 / 视频只筛我的作品，所以只在那个 tab 下出现；Templates 下换成去 AI Effects / AI Tools 的链接 */
export function HistoryFeedTabs({
	view,
	onViewChange,
	showHistory,
	filter,
	onFilterChange,
}: {
	view: FeedView;
	onViewChange: (view: FeedView) => void;
	showHistory: boolean;
	filter: HistoryFilter;
	onFilterChange: (filter: HistoryFilter) => void;
}) {
	const t = useTranslations("assets");
	const tSidebar = useTranslations("saas.sidebar");

	return (
		<div className="flex flex-wrap items-center justify-between gap-2">
			<AssetToolbar
				tabs={[
					{ id: "examples", label: t("tabs.examples") },
					{ id: "templates", label: t("tabs.templates") },
					...(showHistory
						? [{ id: "mine" as const, label: t("tabs.mine") }]
						: []),
				]}
				activeTab={view}
				onTabChange={onViewChange}
			/>
			{view === "mine" ? (
				<HistoryFilterButtons
					filter={filter}
					onFilterChange={onFilterChange}
				/>
			) : view === "templates" ? (
				<div className="flex gap-3">
					{(
						[
							["/ai-effects", tSidebar("aiEffects")],
							["/ai-tools", tSidebar("aiTools")],
						] as const
					).map(([href, label]) => (
						<LocaleLink
							key={href}
							href={href}
							className="flex items-center gap-0.5 text-base-content/60 text-sm transition-colors hover:text-base-content"
						>
							{label}
							<ArrowUpRightIcon className="size-3.5" />
						</LocaleLink>
					))}
				</div>
			) : null}
		</div>
	);
}

/** 全部 / 图片 / 视频，只筛 History */
function HistoryFilterButtons({
	filter,
	onFilterChange,
}: {
	filter: HistoryFilter;
	onFilterChange: (filter: HistoryFilter) => void;
}) {
	const t = useTranslations("assets");
	return (
		<div className="flex gap-1">
			{HISTORY_FILTERS.map((item) => (
				<button
					key={item}
					type="button"
					onClick={() => onFilterChange(item)}
					className={cn(
						"rounded-lg px-2.5 py-1 text-sm transition-colors",
						item === filter
							? "bg-black/5 font-medium text-base-content"
							: "text-base-content/60 hover:text-base-content",
					)}
				>
					{t(`tabs.${item}`)}
				</button>
			))}
		</div>
	);
}
